import {
  CircularProgress,
  MenuItem,
  Paper,
  ScopedCssBaseline,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ThemeProvider,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { OrganizationDashboardUser, Roles } from '@youscience/commonapp-service-common';
import { CoreBox, CoreCheckbox, CoreTooltip, CoreTypography } from '@youscience/core';
import { brightpathTheme } from '@youscience/theme';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { useUserProfile } from '@hooks/useUserProfile';
import { conditionalRedirect } from '@utils/navigation';

import { Order } from '@interfaces';
import { UserRoles } from '@interfaces/user';

import useOrganizationDashboardStore, {
  FilterByRole,
  TableSearchParams,
  UserToUpdate,
} from '@store/OrganizationDashboardStore';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { ConfirmationMessage } from '@components/ConfirmationMessage';
import ScrollToTopButton from '@components/ScrollToTopButton';
import { Search } from '@components/Search';

import { ALL_TYPES_VALUE } from '@constants';
import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { ECP_APPS_URL } from '@constants/externalRoutes';
import { CHECKBOX_TOOLTIP_TEXT, HEAD_CELLS } from '@constants/organizationDashboard';
import { ROUTES } from '@constants/ROUTES';

import CrossIcon from '@images/delete-filter.svg?react';
import MarkIcon from '@images/mark.svg?react';
import PencilIcon from '@images/pencil.svg?react';
import WarningAmberIcon from '@images/Recommender/warning-amber.svg?react';

import {
  StyledEditRolesButton,
  StyledFilterSelect,
  StyledHeadSectionInner,
  StyledHeadTitlesWrap,
  StyledRowEditIcon,
  StyledToolbar,
  StyledTotalEducators,
  sxStyles,
} from './OrganizationDashboard.styles';

type UserId = UserToUpdate['userId'];
const getMapValues = <K, V>(map: Map<K, V>) => Array.from(map.values());

export const OrganizationDashboard = () => {
  const { organizationName = '', role, userId: sessionUserId } = useUserProfile();
  const {
    isLoadingTableData,
    tableData,
    tableSearchParams,
    setTableSearchParams,
    getTableData,
    updateUsers,
    setTableRow,
  } = useOrganizationDashboardStore((store) => store);

  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [isEditingSingleUser, setIsEditingSingleUser] = useState(false);
  const [usersInEdition, setUsersInEdition] = useState<Map<UserId, UserToUpdate>>(new Map());
  const [currentRowInEdition, setCurrentRowInEdition] = useState<OrganizationDashboardUser | null>(null);
  const [isOpenConfirmationMessage, setIsOpenConfirmationMessage] = useState(false);

  const hasEditedSomeUser = usersInEdition.size > 0;
  const currentUserIsAdmin = role === UserRoles.Admin;
  const isEditingOwnUser = currentRowInEdition?.userId === sessionUserId;

  const tableDataAsArray = useMemo(() => getMapValues(tableData), [tableData]);
  const usersToUpdate = useMemo(() => getMapValues(usersInEdition), [usersInEdition]);

  const confirmationDialogProps = {
    onSingleEdit: {
      title: isEditingOwnUser
        ? 'Remove all your roles?'
        : `Remove all roles for this user: ${currentRowInEdition?.legalName}?`,
      subtitle: isEditingOwnUser
        ? 'When you remove all your roles no students will be able to add you as their recommender and you will no longer have access to the Common App requests management.'
        : 'Removing all roles for this user will prevent any students from selecting them as a recommender and will revoke their access to manage Common App requests.',
      subtitle2: isEditingOwnUser
        ? 'After confirming you will be navigated to "Education and Career planning for your student" space.'
        : '',
    },
    onBulkEdit: {
      title: 'Confirm all roles removal.',
      subtitle: 'It seems you have unassigned both roles from some users.',
      subtitle2:
        'If all roles are removed from a user, students will be unable to add them as recommenders, and those users will lose access to the Common App requests.',
      goNextButtonTitle: 'Confirm',
    },
  };

  const currentConfirmationDialogProps = isEditingSingleUser
    ? confirmationDialogProps.onSingleEdit
    : confirmationDialogProps.onBulkEdit;

  const resetUserEdition = () => {
    setUsersInEdition(new Map());
    setCurrentRowInEdition(null);
    setIsEditingSingleUser(false);
  };

  const handleChangeApplicationFilter = (event: SelectChangeEvent<unknown>) => {
    updateSearchParameters({ filterByRole: event.target.value as FilterByRole });
  };

  const updateSearchParameters = (params: Partial<TableSearchParams>) => {
    setTableSearchParams(params);

    void getTableData();
  };

  useEffect(() => {
    void getTableData();
  }, []);

  const handleLegalNameSort = () => {
    const isAsc = tableSearchParams.sortDirection === Order.asc;

    updateSearchParameters({ sortDirection: isAsc ? Order.desc : Order.asc });
  };

  const handleSearch = (value: string) => {
    if (isEditingSingleUser) handleCancelEdition();

    updateSearchParameters({ searchValue: value });
  };

  const handleResetSearch = () => updateSearchParameters({ searchValue: undefined });

  const hasUsersWithoutRoles = (): boolean => {
    const someUserWithoutRoles = usersToUpdate.some(({ roles }) => roles.length === 0);
    if (someUserWithoutRoles) setIsOpenConfirmationMessage(true);
    return someUserWithoutRoles;
  };

  const handleCancelEdition = () => {
    getTableData();
    resetUserEdition();
    setIsBulkEditing(false);
  };

  const handleSingleUserEdit = async (selectedRow: OrganizationDashboardUser) => {
    if (hasUsersWithoutRoles()) return;

    setIsEditingSingleUser(!isEditingSingleUser);

    const isCurrentRowInEdition = currentRowInEdition?.userId === selectedRow.userId;
    if (!isCurrentRowInEdition) {
      getTableData();
      setUsersInEdition(new Map());
      setCurrentRowInEdition(selectedRow);
      return;
    }

    if (hasEditedSomeUser) {
      await updateUsers(usersToUpdate);
      getTableData();
    }

    resetUserEdition();
  };

  const handleBulkEdit = async () => {
    if (!currentUserIsAdmin) return;
    if (hasUsersWithoutRoles()) return;

    setIsBulkEditing(!isBulkEditing);

    if (hasEditedSomeUser) {
      setCurrentRowInEdition(null);

      await updateUsers(usersToUpdate);
      getTableData();
    }

    resetUserEdition();
  };

  const handleRemoveLastRole = async () => {
    updateUsers(usersToUpdate);
    resetUserEdition();
    setIsBulkEditing(false);

    if (isEditingOwnUser) {
      conditionalRedirect(ECP_APPS_URL, 'replace');
    }
  };

  const handleCheckBoxChange = async (
    event: ChangeEvent<HTMLInputElement>,
    checkedRoleValue: boolean,
    selectedRow: OrganizationDashboardUser,
  ) => {
    setCurrentRowInEdition(selectedRow);

    const selectedRoleName = event.target.value as Roles;
    const currentUserRoles = selectedRow.roles;

    const roles = Object.values(Roles);
    const checkedRoles = {} as Record<Roles, boolean>;
    roles.forEach((role) => {
      checkedRoles[role as Roles] = currentUserRoles?.includes(role as Roles);
    });

    checkedRoles[selectedRoleName] = checkedRoleValue;

    const rolesToUpdate = Object.entries(checkedRoles).flatMap(([key, value]) => (value ? [key as Roles] : []));

    const userToUpdate = {
      userId: selectedRow.userId,
      roles: rolesToUpdate,
    };

    const fullUser = {
      ...selectedRow,
      roles: rolesToUpdate,
    };

    const usersInEditionClone = new Map(usersInEdition);
    usersInEditionClone.set(selectedRow.userId, userToUpdate);
    setUsersInEdition(usersInEditionClone);
    setTableRow(fullUser);
  };

  return (
    <ThemeProvider theme={brightpathTheme}>
      <ScopedCssBaseline />

      <StyledHeadSectionInner>
        <Breadcrumbs
          links={[
            { to: `${ROUTES.RECOMMENDER_SPACE}${location.hash}`, text: BREADCRUMBS_PAGE_TEXT.RECOMMENDER_WORKSPACE },
          ]}
          currentPage={BREADCRUMBS_PAGE_TEXT.ORGANIZATION_DASHBOARD}
        />

        <CoreTypography variant="overline">{organizationName}</CoreTypography>

        <StyledHeadTitlesWrap>
          <CoreTypography sx={sxStyles.headTitle} variant="h3">
            Role management: Common App recommenders
          </CoreTypography>
          <CoreTypography sx={sxStyles.headSubtitle}>
            Use this dashboard to manage Common App recommender role assignments within your organization.
          </CoreTypography>
          <CoreTypography sx={sxStyles.headSubtitle}>
            In Common App,{' '}
            <CoreTooltip
              title="
            Counselors share information about a student within the context of their entire class and school community. Counselors submit
             several different forms, including transcripts. The counselor recommendation letter provides insights into a student's academic
              history. Counselors can also share information about achievements, extracurricular accomplishments, and personal interests."
              sx={{ maxWidth: '1000px' }}
            >
              <CoreTypography display="inline" sx={{ textDecoration: 'underline' }}>
                counselors
              </CoreTypography>
            </CoreTooltip>{' '}
            and{' '}
            <CoreTooltip
              title="
            Teachers provide information about a student in the context of their classroom. Teachers have firsthand knowledge of
             a student's intellectual curiosity, creative thought, and writing quality. They can also contextualize a student's
              class behavior and work habits. If requested, teachers submit evaluations (letters of recommendation) for students."
              sx={{ maxWidth: '1000px' }}
            >
              <CoreTypography display="inline" sx={{ textDecoration: 'underline' }}>
                teachers
              </CoreTypography>
            </CoreTooltip>{' '}
            belong to a group of user roles called recommenders. Students can view and add recommenders in their Common
            App profile.
          </CoreTypography>
          <CoreTypography sx={sxStyles.headSubtitle}>
            NOTE: Recommender roles can only be unassigned after a student removes the user as a counselor or teacher
            from their application profile.
          </CoreTypography>
        </StyledHeadTitlesWrap>
      </StyledHeadSectionInner>

      <CoreBox sx={sxStyles.tableWrap}>
        <StyledToolbar>
          <CoreBox sx={sxStyles.flexAlignPosition}>
            <CoreTypography>Role:</CoreTypography>

            <StyledFilterSelect
              data-testid="filterByRole"
              value={tableSearchParams.filterByRole}
              onChange={handleChangeApplicationFilter}
            >
              <MenuItem value={ALL_TYPES_VALUE}>All</MenuItem>

              {Object.values(Roles).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </StyledFilterSelect>
          </CoreBox>

          <CoreBox sx={sxStyles.flexAlignPosition}>
            <StyledTotalEducators>{`Total number of educators: ${tableData?.size ?? 0}`}</StyledTotalEducators>

            <CoreBox sx={sxStyles.searchWrap}>
              <Search
                filterValue={tableSearchParams.searchValue}
                onEnterSearch={handleSearch}
                onClear={handleResetSearch}
              />
            </CoreBox>

            {currentUserIsAdmin ? (
              <>
                {isBulkEditing ? (
                  <StyledEditRolesButton
                    size="medium"
                    variant="outlined"
                    data-testid={`${isBulkEditing ? 'active' : 'disabled'}-cancel-button`}
                    onClick={handleCancelEdition}
                    disabled={isEditingSingleUser}
                  >
                    Cancel
                  </StyledEditRolesButton>
                ) : null}

                <StyledEditRolesButton
                  size="medium"
                  variant="contained"
                  data-testid={`${isBulkEditing ? 'active' : 'disabled'}-edit-button`}
                  onClick={handleBulkEdit}
                  startIcon={isBulkEditing ? <MarkIcon /> : <PencilIcon />}
                  disabled={isEditingSingleUser}
                >
                  {isBulkEditing ? 'Save changes' : 'Edit roles'}
                </StyledEditRolesButton>
              </>
            ) : null}
          </CoreBox>
        </StyledToolbar>

        <Paper sx={sxStyles.paper}>
          <TableContainer>
            <Table aria-labelledby="organization-dashboard-table">
              <TableHead>
                <TableRow sx={sxStyles.headRow}>
                  {HEAD_CELLS.map((headCell) => {
                    if (headCell.id === 'legalName') {
                      return (
                        <TableCell key={headCell.id} sortDirection={tableSearchParams.sortDirection}>
                          <TableSortLabel
                            active
                            direction={tableSearchParams.sortDirection}
                            onClick={handleLegalNameSort}
                          >
                            {headCell.label}

                            <CoreBox component="span" sx={visuallyHidden}>
                              {tableSearchParams.sortDirection === Order.desc
                                ? 'sorted descending'
                                : 'sorted ascending'}
                            </CoreBox>
                          </TableSortLabel>
                        </TableCell>
                      );
                    } else {
                      return (
                        <TableCell
                          key={headCell.id}
                          align={
                            headCell.id === 'counselorAssign' || headCell.id === 'teacherAssign' ? 'center' : 'left'
                          }
                        >
                          {headCell.label}
                        </TableCell>
                      );
                    }
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData?.size === 0 && !isLoadingTableData ? (
                  <TableRow sx={sxStyles.tableBodyRow}>
                    <TableCell colSpan={5}>
                      <CoreTypography sx={{ textAlign: 'center' }}>No data available</CoreTypography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {tableDataAsArray.map((tableRow) => {
                      const isRowEdit = tableRow.userId === currentRowInEdition?.userId;
                      const isEditing = isRowEdit || isBulkEditing;
                      const isUserCanChangeRole = currentUserIsAdmin || tableRow.userId === sessionUserId;
                      const { isAssignedCounselor, isAssignedTeacher, isAssignedDifferentTenant, roles } = tableRow;
                      const canEditRoles = !isAssignedDifferentTenant && (!isAssignedCounselor || !isAssignedTeacher);

                      return (
                        <TableRow key={tableRow.userId} sx={sxStyles.tableBodyRow}>
                          <TableCell>{tableRow.legalName}</TableCell>

                          <TableCell>{tableRow.email}</TableCell>

                          <TableCell align="center">
                            <CoreTooltip
                              title={isEditing && isAssignedCounselor ? CHECKBOX_TOOLTIP_TEXT : ''}
                              disableInteractive
                            >
                              <CoreBox>
                                <CoreCheckbox
                                  checked={roles.includes(Roles.COUNSELOR)}
                                  value={Roles.COUNSELOR}
                                  disabled={
                                    isLoadingTableData || !isEditing || isAssignedCounselor || isAssignedDifferentTenant
                                  }
                                  onChange={(event, checkedValue) =>
                                    handleCheckBoxChange(event, checkedValue, tableRow)
                                  }
                                />
                              </CoreBox>
                            </CoreTooltip>
                          </TableCell>

                          <TableCell align="center">
                            <CoreTooltip
                              title={isEditing && isAssignedTeacher ? CHECKBOX_TOOLTIP_TEXT : ''}
                              disableInteractive
                            >
                              <CoreBox>
                                <CoreCheckbox
                                  checked={roles.includes(Roles.TEACHER)}
                                  value={Roles.TEACHER}
                                  disabled={
                                    isLoadingTableData || !isEditing || isAssignedTeacher || isAssignedDifferentTenant
                                  }
                                  onChange={(event, checkedValue) =>
                                    handleCheckBoxChange(event, checkedValue, tableRow)
                                  }
                                />
                              </CoreBox>
                            </CoreTooltip>
                          </TableCell>

                          <TableCell align="center">
                            {isUserCanChangeRole && !isBulkEditing && canEditRoles ? (
                              <CoreBox sx={{ display: 'flex' }}>
                                {isRowEdit ? (
                                  <StyledRowEditIcon sx={sxStyles.cancelEditIcon} onClick={handleCancelEdition}>
                                    <CrossIcon />
                                  </StyledRowEditIcon>
                                ) : null}

                                <StyledRowEditIcon onClick={() => handleSingleUserEdit(tableRow)}>
                                  {isEditing ? <MarkIcon /> : <PencilIcon />}
                                </StyledRowEditIcon>
                              </CoreBox>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}
              </TableBody>
            </Table>

            {isLoadingTableData ? (
              <CoreBox data-testid="progressIndicator" sx={sxStyles.loader}>
                <CircularProgress size={50} thickness={3} />
              </CoreBox>
            ) : null}
          </TableContainer>
        </Paper>
      </CoreBox>

      <ConfirmationMessage
        icon={<WarningAmberIcon />}
        isOpenConfirmationMessage={isOpenConfirmationMessage}
        setIsOpenConfirmationMessage={setIsOpenConfirmationMessage}
        onGoNextBtnClick={handleRemoveLastRole}
        {...currentConfirmationDialogProps}
      />

      <ScrollToTopButton />
    </ThemeProvider>
  );
};
